/* @flow */

import type { InfoBlock } from "shop-state/types";

import React, { useContext, useEffect, useMemo } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { useData, useSendMessage } from "crustate/react";
import { Helmet } from "react-helmet-async";
import { HomeData, CustomerData, CurrentPageInfoData, CurrentInfoListData, AffiliateListSelectedData } from "data";
import ProductCard, { DummyCard } from "components/ProductCard";
import ProductCarousel from "components/ProductCarousel";
import PopularCategoryCarousel from "components/PopularCategoryCarousel";
import CurrentPageInfoSection from "./CurrentPageInfoSection";
import UploadImageSection from "components/HomeView/UploadImageSection";
import Wrapper from "components/Wrapper";
import HomeHero from "components/HomeHero";
import useBrowserDimensions from "helpers/use-browser-dimensions";
import { getCurrentPageInfo } from "state/current-page-info";
import { StoreInfoContext } from "entrypoint/shared";
import { Title, Description, Ingress, Items, Item } from "components/UiComponents";
import { getCustomerData, getNumberBasedOnBrowserWidth, tryParseJSONObject } from "helpers/utils";
import AffiliateList, { AffiliateDummyList } from "components/AffiliateList";
import USPSection from "components/USPSection";
import useNotifyMissingAccountDetails from "helpers/use-notify-missing-account-details";
import { useTranslate } from "@awardit/react-use-translate";

import styles from "./styles.scss";

const HomeView = (): React$Node => {
  const {
    routes,
    content: {
      homeview: content,
      allproductsview: { popularCategories, popularCategoriesTitle },
      productCarousel: { useOnHomeView },
    },
  } = useContext(StoreInfoContext);
  const home = useData(HomeData);
  const affiliateList = useData(AffiliateListSelectedData);
  const sendMessage = useSendMessage();
  const t = useTranslate();
  const { width: browserWidth } = useBrowserDimensions();
  const customer = getCustomerData(useData(CustomerData));
  const currentPageInfoData = useData(CurrentPageInfoData);
  const currentInfoListData = useData(CurrentInfoListData);
  const popularCategoriesJson = popularCategories !== undefined &&
    tryParseJSONObject(popularCategories) ? JSON.parse(popularCategories) : "";

  const memberTargetList = customer &&
    customer.memberTargetList &&
    customer.memberTargetList.list.length > 0 ?
    customer.memberTargetList.list : [];

  const currentInfoArray = currentInfoListData.state === "LOADED" &&
    currentInfoListData.data &&
    currentInfoListData.data.length > 0 ? currentInfoListData.data : [];

  const currentInfoRoute = routes.currentInfoViewHome &&
    routes.currentInfoViewHome.toggle !== undefined &&
    routes.currentInfoViewHome.toggle &&
    typeof routes.currentInfoViewHome.url === "string" &&
    routes.currentInfoViewHome.url.length > 0 ? routes.currentInfoViewHome.url : null;

  const numAffiliates = getNumberBasedOnBrowserWidth(
    styles,
    { small: 4, medium: 6 },
    8,
    browserWidth
  );

  useEffect(() => {
    sendMessage(getCurrentPageInfo("HOMEVIEW"));
    sendMessage(getCurrentPageInfo("NEWSVIEW"));
    sendMessage(getCurrentPageInfo("OFFERLISTVIEW"));
    sendMessage(getCurrentPageInfo("CURRENTINFO6"));
  }, []);

  useNotifyMissingAccountDetails();

  const currentInfoOrder = content.currentInfoOrder ?
    Number.parseInt(content.currentInfoOrder, 10) :
    1;
  const featuredContentOrder = content.featuredContentOrder ?
    Number.parseInt(content.featuredContentOrder, 10) :
    2;
  const featuredProductsOrder = content.featuredProductsOrder ?
    Number.parseInt(content.featuredProductsOrder, 10) :
    3;
  const howToEarnOrder = content.howToEarnOrder ?
    Number.parseInt(content.howToEarnOrder, 10) :
    4;
  const uploadImageOrder = content.uploadImageOrder ?
    Number.parseInt(content.uploadImageOrder, 10) :
    5;
  const additionalHtmlOrder = content.additionalHtmlOrder ?
    Number.parseInt(content.additionalHtmlOrder, 10) :
    6;
  const additionalHtmlSecondaryOrder = content.additionalHtmlSecondaryOrder ?
    Number.parseInt(content.additionalHtmlSecondaryOrder, 10) :
    7;
  const earnOnlineOrder = content.earnOnlineOrder ?
    Number.parseInt(content.earnOnlineOrder, 10) :
    8;
  const popularProductsOrder = content.popularProductsOrder ?
    Number.parseInt(content.popularProductsOrder, 10) :
    -1;
  const popularCategoriesOrder = content.popularCategoriesOrder ?
    Number.parseInt(content.popularCategoriesOrder, 10) :
    -1;
  const currentInfo6Order = content.currentInfo6Order ?
    Number.parseInt(content.currentInfo6Order, 10) :
    -1;
  const getItemAmount = (config, defaultAmount) => {
    if (browserWidth > 0) {
      const currentBreakpoint = Object.keys(config).find(breakpoint => {
        return browserWidth <= parseInt(styles[breakpoint], 10);
      });

      return currentBreakpoint ? config[currentBreakpoint] : defaultAmount;
    }

    return 0;
  };

  const featuredProductsAmount = getItemAmount({ small: 2, medium: 3 }, 4);

  const featuredProducts = useMemo(() => home.state !== "LOADED" ? [...new Array(4)] :
    home.data.featuredProducts && home.data.featuredProducts.filter(p => !memberTargetList ||
      memberTargetList.includes(p.attributes.awarditTargetId) ||
      !p.attributes.awarditTargetId)
      .sort(() => 0.5 - Math.random())
      .slice(0, featuredProductsAmount
      ), [home.state, featuredProductsAmount]);

  const currentInfoHomeView: $ReadOnlyArray<InfoBlock> =
    currentPageInfoData.state === "LOADED" ? currentPageInfoData.data.homeview || [] : [];

  const affiliateItems = (affiliateList.state === "LOADED" ? affiliateList.data : []).slice(0, numAffiliates);

  const currentInfo6Count = content.currentInfo6Count ?
    Number.parseInt(content.currentInfo6Count, 10) : 0;

  const currentInfo6Array = (
    currentPageInfoData.state === "LOADED" &&
    currentPageInfoData.data.currentinfo6 &&
    currentPageInfoData.data.currentinfo6.length > 0
  ) ? (currentInfo6Count > 0 ?
      currentPageInfoData.data.currentinfo6.slice(0, currentInfo6Count) :
      currentPageInfoData.data.currentinfo6
    ) : [];

  const earnOnlineSection = (
    (content.earnOnlineHeading && affiliateList.state === "LOADED" &&
    <Wrapper key="earnOnline" className={cn(styles.section, styles.wrapper, styles.earnOnlineSection)}>
      <div className={styles.top}>
        {content.earnOnlineHeading &&
          <Title className={styles.heading} elem="h2">{content.earnOnlineHeading}</Title>
        }
        {content.earnOnlineButtonLink && content.earnOnlineButtonText &&
        <div className={styles.earnOnline}>
          <Link
            to={content.earnOnlineButtonLink}
            className={cn("link", styles.cta)}
          >
            {content.earnOnlineButtonText}
          </Link>
        </div>
        }
      </div>
      {affiliateList.state === "LOADED" &&
        <AffiliateList items={affiliateItems} />
      }

      {affiliateList.state === "LOADING" &&
        <AffiliateDummyList items={Array.from({
          length: numAffiliates,
        }, () => null)} />
      }
    </Wrapper>
    )
  );

  const featuredProductsSection = (
    <Wrapper className={cn(styles.wrapper, "awardit-featuredProductsSection")}>
      <div key="featuredProducts" className={styles.section}>
        <div className={styles.top}>
          {(content.featuredProductsHeading || content.featuredProductsDescription) && (
            <Ingress className={styles.ingress}>
              {content.featuredProductsHeading && (
                <Title className={styles.heading} elem="h2">{content.featuredProductsHeading}</Title>
              )}
              {content.featuredProductsDescription && (
                <Description
                  className={cn(styles.description, "awardit-description")}
                  content={content.featuredProductsDescription ?? ""}
                />
              )}
            </Ingress>
          )}
          {content.featuredProductsButtonLink && content.featuredProductsButtonText &&
            <Link
              to={content.featuredProductsButtonLink}
              className={cn("link", styles.cta)}
            >
              {content.featuredProductsButtonText}
            </Link>
          }
        </div>
        <Items className={styles.featuredProductsList}>
          {featuredProducts && featuredProducts.map((p, i) =>
            p ?
              <Item key={p.name} className={styles.featuredProduct}>
                <ProductCard product={p} list={content.featuredProductsHeading ?? ""} position={i} />
              </Item> :
              <Item key={i}><DummyCard /></Item>
          )}
        </Items>
      </div>
    </Wrapper>
  );

  const featuredContentSection = (
    currentInfoArray &&
    currentInfoArray.length > 0 &&
    <div key="featuredContent" className={cn(styles.featuredContentSection, "awardit-featuredContentSection")}>
      <Wrapper className={styles.darkBgWrapper}>
        {content.currentInfoTitle &&
          <Title className={styles.heading} elem="h2">{content.currentInfoTitle}</Title>
        }
        <div className={styles.currentPageInfoFeatured}>
          {currentInfoArray.map((info, i) => (
          /* eslint-disable react/no-danger */
            <div key={"offerlist_item_" + i} className={styles.currentInfoCardWrapper} idx={i}>
              <div className={cn(styles.currentInfoCard, "awardit-currentInfoCard")}>
                {routes.currentInfoView1 && routes.currentInfoView1.url &&
                  <Link
                    className={styles.currentInfoCardLink}
                    aria-label={info.title}
                    to={{
                      pathname: `${routes.currentInfoView1.url}/${info.id}`,
                      state: { hint: { title: info.title } },
                    }}
                  />
                }
                <div><img src={info.image} /></div>
                {info.fromDate &&
                  <p className={styles.currentInfoCardDate}>{info.fromDate}</p>
                }
                <div className={styles.currentInfoCardContent}>
                  <h3>{info.title}</h3>
                  <div
                    className={cn(styles.currentInfoDescription, "awardit-currentInfoDescription")}
                    dangerouslySetInnerHTML={{ __html: info.content }} />
                </div>
              </div>
            </div>
          /* eslint-enable react/no-danger */
          ))}
        </div>
      </Wrapper>
    </div>
  );

  const currentPageInfo = currentPageInfoData.state === "LOADED" &&
    currentInfoHomeView.length > 0 && (
    <div key="currentPageInfo" className="awardit-currentPageInfoSection">
      <Wrapper className={styles.wrapper}>
        {(content.currentInfoTitle || currentInfoRoute) &&
        <div className={styles.top}>
          {content.currentInfoTitle &&
            <Title className={styles.heading} elem="h2">{content.currentInfoTitle}</Title>
          }
          {currentInfoHomeView.length > 5 && currentInfoRoute &&
            <Link className={styles.cta} to={currentInfoRoute}>
              {t("HOMEVIEW.MORE_NEWS")}
            </Link>
          }
        </div>
        }
        <CurrentPageInfoSection
          items={currentInfoHomeView}
          route={currentInfoRoute}
        />
      </Wrapper>
    </div>
  );

  const howToEarnSection =
    (content.howToEarnDescription || content.howToEarnHeading || content.howToEarnImage) ? (
      <Wrapper className={styles.wrapper}>
        <div key="howToEarn" className={styles.promotion}>
          <div className={cn(styles.left, !content.howToEarnImage ? styles.empty : "")}>
            {content.howToEarnImage &&
              <img src={content.howToEarnImage} />
            }
          </div>
          <div className={styles.right}>
            {content.howToEarnHeading &&
              <Title className={styles.heading} elem="h2">{content.howToEarnHeading}</Title>
            }
            {content.howToEarnDescription &&
              <Description className={cn(styles.description, "awardit-description")} content={content.howToEarnDescription ?? ""} />
            }
            {content.howToEarnButtonLink && content.howToEarnButtonText &&
              <Link
                to={content.howToEarnButtonLink}
                className={cn("link", styles.ctaLarge)}
              >
                {content.howToEarnButtonText}
              </Link>
            }
          </div>
        </div>
      </Wrapper>
    ) : null;

  const popularCategoriesSection = (
    (popularCategoriesJson &&
    <Wrapper className={cn(styles.popularCategories, styles.wrapper, "awardit-popularCategories")}>
      <PopularCategoryCarousel
        title={popularCategoriesTitle ?? ""}
        popularCategories={popularCategoriesJson}
      />
    </Wrapper>
    )
  );

  const popularProductsSection = (
    Boolean(useOnHomeView) && (
      <Wrapper className={cn(styles.popularProducts, styles.wrapper, "awardit-popularProducts")}>
        <ProductCarousel allProductsLink />
      </Wrapper>
    )
  );

  const additionalHtmlSection = (
    content.additionalHtml && (
      <div className={cn(styles.additionalHtmlContainer, "awardit-additionalHtmlContainer")}>
        {/* eslint-disable react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: content.additionalHtml }} />
        {/* eslint-enable react/no-danger */}
      </div>
    )
  );

  const additionalHtmlSecondarySection = (
    content.additionalHtmlSecondary && (
      <div className={styles.additionalHtmlContainer}>
        {/* eslint-disable react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: content.additionalHtmlSecondary }} />
        {/* eslint-enable react/no-danger */}
      </div>
    )
  );

  const currentInfo6Section = (
    currentInfo6Array &&
    currentInfo6Array.length > 0 &&
    routes.currentInfoView6?.url && (
      <div className="awardit-currenInfoCardsWrapper">
        <Wrapper className={styles.wrapper}>
          {content.currentInfo6Title &&
            <Title className={styles.heading} elem="h2">{content.currentInfo6Title}</Title>
          }
          <div className={styles.currenInfoCardsSection}>
            {currentInfo6Array.map((info, i) => (
            /* eslint-disable react/no-danger */
              <div key={"currentinfo6_item_" + i} className={cn(styles.currentInfoCardWrapper, styles.currentInfoCardWrapperAlt)} idx={i}>
                <Link
                  to={{
                    pathname: `${(routes.currentInfoView6 && routes.currentInfoView6.url) ?? ""}/${info.id}`,
                    state: { hint: { title: info.title } },
                  }}
                  className={cn(styles.currentInfoCard, styles.currentInfoCardAlt, "awardit-currenInfoCard")}
                >
                  {info.title && <h3>{info.title}</h3>}
                  {info.image &&
                    <div className={styles.currenInfoCardImage}>
                      <img src={info.image} />
                    </div>
                  }
                  {info.content &&
                    <div
                      className={cn(styles.currentInfoDescription, "awardit-currentInfoDescription")}
                      dangerouslySetInnerHTML={{ __html: info.description }}
                    />
                  }
                </Link>
              </div>
            /* eslint-enable react/no-danger */
            ))}
          </div>
        </Wrapper>
      </div>
    )
  );

  const main = [];

  if (featuredProductsOrder >= 0) {
    main[featuredProductsOrder] = featuredProductsSection;
  }

  if (currentInfoOrder >= 0) {
    main[currentInfoOrder] = currentPageInfo;
  }

  if (featuredContentOrder >= 0) {
    main[featuredContentOrder] = featuredContentSection;
  }

  if (uploadImageOrder >= 0) {
    main[uploadImageOrder] = (
      routes.photoServiceView &&
      routes.photoServiceView.toggle !== undefined &&
      routes.photoServiceView.toggle &&
      <Wrapper className={styles.wrapper}>
        <UploadImageSection />
      </Wrapper>
    );
  }

  if (additionalHtmlOrder >= 0) {
    main[additionalHtmlOrder] = additionalHtmlSection;
  }

  if (additionalHtmlSecondaryOrder >= 0) {
    main[additionalHtmlSecondaryOrder] = additionalHtmlSecondarySection;
  }

  if (popularProductsOrder >= 0) {
    main[popularProductsOrder] = popularProductsSection;
  }

  if (popularCategoriesOrder >= 0) {
    main[popularCategoriesOrder] = popularCategoriesSection;
  }

  if (earnOnlineOrder >= 0) {
    main[earnOnlineOrder] = earnOnlineSection;
  }

  if (howToEarnOrder >= 0) {
    main[howToEarnOrder] = howToEarnSection;
  }

  if (currentInfo6Order >= 0) {
    main[currentInfo6Order] = currentInfo6Section;
  }

  return (
    <div className={cn(styles.block, "awardit-homeView")}>
      <Helmet title={content.pageTitle ?? ""} />
      <HomeHero />
      <div className={cn(styles.homeViewContent, "awardit-homeViewContent")}>
        <USPSection />
        { main }
      </div>
    </div>
  );
};

export default HomeView;
